import { submitApplication, type ApplicationPayload } from '../lib/applications-api';
import { supabase } from '../lib/supabase';
import { getOptionalSession } from '../lib/auth-guard';
import { publicRedirect } from '../lib/canonical';
import { rememberReturnTo } from '../lib/return-to';
import { getTurnstileToken, captchaOptions, turnstileEnabled } from '../lib/turnstile';

// Public self-serve signup, rebuilt 2026-08-05 as a two-step flow. Step 1 asks
// only the three required fields (name, type, email); step 2 collects optional
// detail and can be skipped. ONE submit fires at the end — the wire payload and
// the submit-provider-application Edge Function contract are unchanged; fields
// the form no longer collects (phone, follower counts, facebook/tiktok,
// address, country, experience, why_join) are sent as null, exactly as an
// applicant leaving them blank always produced.
//
// Business types: the 13 canonical values are grouped behind six broad chips.
// A chip whose group has one value selects it directly; a multi-value group
// reveals a sub-chip row and requires the specific pick, so the wire value is
// always one of the 13 — no remapping, no new values.

interface TypeGroup {
  readonly id: string;
  readonly label: string;
  readonly subs: ReadonlyArray<readonly [string, string]>;
}

// Every value below must exist in PROVIDER_TYPE_OPTIONS (applications-api.ts);
// the union of subs covers all 13 exactly once.
const TYPE_GROUPS: readonly TypeGroup[] = [
  {
    id: 'food',
    label: 'Food & drink',
    subs: [
      ['restaurant', 'Restaurant'],
      ['bar', 'Bar'],
      ['food_drink', 'Café, deli or gelateria'],
    ],
  },
  {
    id: 'tours',
    label: 'Tours & activities',
    subs: [
      ['walking_tours', 'Walking tours'],
      ['bike_tours', 'Bike tours'],
      ['food_tours', 'Food tours'],
      ['wine_tours', 'Wine tours'],
      ['activity', 'Classes & workshops'],
    ],
  },
  {
    id: 'bikes',
    label: 'Bike services',
    subs: [
      ['mechanic', 'Bike shop / mechanic'],
      ['mobile_mechanic', 'Mobile mechanic'],
    ],
  },
  { id: 'venue', label: 'Venue & events', subs: [['venue', 'Venue or event space']] },
  { id: 'creator', label: 'Creator', subs: [['influencer', 'Influencer / content creator']] },
  { id: 'other', label: 'Something else', subs: [['other', 'Other']] },
];

// Sign-in comes first (WP2). The application now carries the applicant's real
// auth.uid(), which is what lets sync_provider_profile_from_application key the
// resulting profile to an account that can actually sign in to it. The anonymous
// path is gone: it is exactly what produced the 5 orphan profiles on MAIN.
export async function renderApply(container: HTMLElement): Promise<void> {
  const session = await getOptionalSession();
  if (!session) {
    renderApplySignIn(container);
    return;
  }
  renderApplyForm(container, session.access_token, session.user.email ?? null);
}

function renderApplySignIn(container: HTMLElement): void {
  container.innerHTML = `
    <main class="cl-shell">
      <div class="cl-card">
        <div class="cl-logo">Curate</div>
        <h1>Put your business on the map</h1>
        <p class="cl-lead">Start with your email — we&rsquo;ll send a one-tap sign-in link, then
          three quick questions. No password to remember.</p>
        <form id="ap-signin">
          <label for="ap-signin-email">Email</label>
          <input type="email" id="ap-signin-email" name="email" required autocomplete="email"
                 inputmode="email" placeholder="you@yourbusiness.com" />
<div class="ts-mount" id="ts-apply"></div>
          <button type="submit" class="cl-btn">Send me a sign-in link</button>
        </form>
        <p id="ap-signin-status" class="status" aria-live="polite"></p>
        <p class="cl-foot">Already listed with us? <a href="#/claim">Claim your business</a>.</p>
      </div>
    </main>
  `;

  const form = container.querySelector<HTMLFormElement>('#ap-signin');
  const status = container.querySelector<HTMLElement>('#ap-signin-status');
  if (!form || !status) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = String(new FormData(form).get('email') ?? '').trim().toLowerCase();
    if (!email) return;
    status.textContent = 'Sending…';
    status.className = 'status';
    rememberReturnTo('#/apply');
    // Turnstile is inert unless VITE_TURNSTILE_SITE_KEY is set; a failed or
    // unavailable challenge yields null and the request proceeds. The
    // server-side caps are the control that always holds (WP5).
    const tsMount = container.querySelector<HTMLElement>('#ts-apply');
    const tsToken = tsMount ? await getTurnstileToken(tsMount) : null;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: publicRedirect(), shouldCreateUser: true, ...captchaOptions(tsToken) },
    });
    if (error) {
      status.textContent = `Sorry — ${error.message}. Try again, or email john@ebikesflorence.com.`;
      status.className = 'status status-error';
      return;
    }
    status.textContent = `Check your inbox for ${email}. The link expires in 60 minutes.`;
    status.className = 'status status-success';
    form.querySelector<HTMLButtonElement>('button[type="submit"]')!.disabled = true;
  });
}

function renderApplyForm(
  container: HTMLElement,
  accessToken: string,
  sessionEmail: string | null,
): void {
  const groupChips = TYPE_GROUPS.map(
    (g) => `<button type="button" class="ap-chip" data-group="${g.id}">${g.label}</button>`,
  ).join('');

  container.innerHTML = `
    <div class="ap-shell">
      <aside class="ap-brand">
        <div class="ap-logo">Curate</div>
        <div class="ap-brand-copy">
          <h1>Put your business on the map — literally.</h1>
          <p class="ap-sub">Curate guides travellers through your city with self-guided audio tours. Partners appear right on the route — at the moment someone walks or rides past your door.</p>
          <ul class="ap-perks">
            <li><span class="ap-tick">✓</span><span><strong>Free to join, zero commission.</strong> You keep what you earn.</span></li>
            <li><span class="ap-tick">✓</span><span><strong>Reach travellers mid-tour</strong> — when they're steps away and deciding what to do next.</span></li>
            <li><span class="ap-tick">✓</span><span><strong>Get featured by local creators</strong> whose tours send people your way.</span></li>
          </ul>
        </div>
        <div class="ap-brand-foot">Curate is operated by E-Bikes Florence. Questions? Email john@ebikesflorence.com.</div>
      </aside>

      <main class="ap-main">
        <div class="ap-top">Already a partner? <a href="#/signin">Sign in</a></div>
        <div class="ap-card">

          <section class="ap-pane ap-show" id="ap-step1" aria-label="Step 1 of 2">
            <div class="ap-steps"><span class="ap-steps-label">Step 1 of 2</span><span class="ap-dot ap-on"></span><span class="ap-dot"></span></div>
            <h2>Let's get you listed</h2>
            <p class="ap-lead">Three quick things and you're in. Takes under a minute.</p>

            <div class="ap-field" id="ap-f-name">
              <label for="business_name">Business name</label>
              <input type="text" id="business_name" name="business_name" autocomplete="organization" placeholder="e.g. Trattoria da Marco" />
              <p class="ap-err">Please add your business name.</p>
            </div>

            <div class="ap-field" id="ap-f-type">
              <label id="ap-type-label">What kind of business?</label>
              <div class="ap-chips" role="group" aria-labelledby="ap-type-label">${groupChips}</div>
              <div class="ap-subwrap" id="ap-subwrap" hidden>
                <label class="ap-sublabel" id="ap-sub-label">Which kind?</label>
                <div class="ap-chips" id="ap-subchips" role="group" aria-labelledby="ap-sub-label"></div>
              </div>
              <p class="ap-err" id="ap-type-err">Pick the closest match — you can refine it later.</p>
            </div>

            <div class="ap-field" id="ap-f-email">
              <label for="contact_email">Email</label>
              <input type="email" id="contact_email" name="contact_email" autocomplete="email" placeholder="you@yourbusiness.com" />
              <p class="ap-err">That email doesn't look quite right.</p>
            </div>

            <button type="button" class="ap-btn" id="ap-continue">Continue</button>
            <p class="ap-note">We personally welcome every new partner — you'll get your sign-in link by email, usually within one business day.</p>
          </section>

          <section class="ap-pane" id="ap-step2" aria-label="Step 2 of 2">
            <div class="ap-steps"><span class="ap-steps-label">Step 2 of 2</span><span class="ap-dot ap-on"></span><span class="ap-dot ap-on"></span></div>
            <h2>Help travellers find you</h2>
            <p class="ap-lead">All optional — a little detail helps us set up your listing faster. You can add or change everything later.</p>

            <div class="ap-field">
              <label for="description">What you do <span class="ap-hint">(one or two lines is plenty)</span></label>
              <textarea id="description" name="description" rows="3" placeholder="e.g. Family-run trattoria near Ponte Vecchio, famous for our bistecca."></textarea>
            </div>

            <div class="ap-cols">
              <div class="ap-field">
                <label for="city">City</label>
                <input type="text" id="city" name="city" autocomplete="address-level2" placeholder="Florence" />
              </div>
              <div class="ap-field">
                <label for="instagram_handle">Instagram</label>
                <input type="text" id="instagram_handle" name="instagram_handle" placeholder="@yourbusiness" />
              </div>
            </div>

            <div class="ap-field">
              <label for="website">Website</label>
              <input type="url" id="website" name="website" placeholder="https://…" />
            </div>

            <button type="button" class="ap-btn" id="ap-finish">Finish</button>
            <button type="button" class="ap-skip" id="ap-skip">Skip for now — I'll add this later</button>
          </section>

          <section class="ap-pane" id="ap-step3" aria-label="All done">
            <div class="ap-success">
              <div class="ap-bigtick">✓</div>
              <h2 id="ap-success-title">You're in!</h2>
              <p>Thanks — we're setting up your listing now. Your sign-in link is on its way to <strong id="ap-success-email"></strong>, usually within one business day.</p>
              <div class="ap-next">
                <h3>What happens next</h3>
                <ol>
                  <li><span class="ap-n">1</span><span>We give your listing a quick once-over — every partner gets a personal welcome.</span></li>
                  <li><span class="ap-n">2</span><span>Your sign-in link lands in your inbox.</span></li>
                  <li><span class="ap-n">3</span><span>You add photos and details, and travellers start finding you on tours.</span></li>
                </ol>
              </div>
            </div>
          </section>

          <p id="apply-status" class="status" aria-live="polite"></p>
        </div>
      </main>
    </div>
  `;

  const q = <T extends HTMLElement>(sel: string): T | null => container.querySelector<T>(sel);
  const step1 = q<HTMLElement>('#ap-step1');
  const step2 = q<HTMLElement>('#ap-step2');
  const step3 = q<HTMLElement>('#ap-step3');
  const status = q<HTMLElement>('#apply-status');
  if (!step1 || !step2 || !step3 || !status) return;

  const setStatus = (message: string, kind: '' | 'success' | 'error'): void => {
    status.textContent = message;
    status.className = kind ? `status status-${kind}` : 'status';
  };

  const showPane = (pane: HTMLElement): void => {
    [step1, step2, step3].forEach((p) => p.classList.remove('ap-show'));
    pane.classList.add('ap-show');
    window.scrollTo({ top: 0 });
  };

  // ── Business-type selection ────────────────────────────────────
  let selectedType = ''; // one of the 13 wire values, '' until chosen

  const subwrap = q<HTMLElement>('#ap-subwrap');
  const subchips = q<HTMLElement>('#ap-subchips');
  const typeField = q<HTMLElement>('#ap-f-type');
  const typeErr = q<HTMLElement>('#ap-type-err');

  const clearInvalid = (el: HTMLElement | null): void => el?.classList.remove('ap-invalid');

  container.querySelectorAll<HTMLButtonElement>('.ap-chip[data-group]').forEach((chip) => {
    chip.addEventListener('click', () => {
      container
        .querySelectorAll<HTMLButtonElement>('.ap-chip[data-group]')
        .forEach((c) => c.classList.remove('ap-selected'));
      chip.classList.add('ap-selected');
      const group = TYPE_GROUPS.find((g) => g.id === chip.dataset.group) ?? null;
      clearInvalid(typeField);

      if (!group || !subwrap || !subchips || !typeErr) return;
      const only = group.subs.length === 1 ? group.subs[0] : undefined;
      if (only) {
        selectedType = only[0];
        subwrap.hidden = true;
        subchips.innerHTML = '';
      } else {
        selectedType = '';
        typeErr.textContent = 'Which kind? One more tap.';
        subchips.innerHTML = group.subs
          .map(
            ([value, label]) =>
              `<button type="button" class="ap-chip ap-chip-sub" data-type="${value}">${label}</button>`,
          )
          .join('');
        subwrap.hidden = false;
        subchips.querySelectorAll<HTMLButtonElement>('.ap-chip-sub').forEach((sub) => {
          sub.addEventListener('click', () => {
            subchips
              .querySelectorAll<HTMLButtonElement>('.ap-chip-sub')
              .forEach((c) => c.classList.remove('ap-selected'));
            sub.classList.add('ap-selected');
            selectedType = sub.dataset.type ?? '';
            clearInvalid(typeField);
          });
        });
      }
    });
  });

  // ── Step 1 → 2 ─────────────────────────────────────────────────
  const nameInput = q<HTMLInputElement>('#business_name');
  const emailInput = q<HTMLInputElement>('#contact_email');
  const nameField = q<HTMLElement>('#ap-f-name');
  const emailField = q<HTMLElement>('#ap-f-email');

  [nameInput, emailInput].forEach((input) => {
    input?.addEventListener('input', () => clearInvalid(input.closest('.ap-field')));
  });

  q<HTMLButtonElement>('#ap-continue')?.addEventListener('click', () => {
    const businessName = nameInput?.value.trim() ?? '';
    const contactEmail = (emailInput?.value.trim() ?? '').toLowerCase();
    let bad = false;
    if (businessName.length < 2) {
      nameField?.classList.add('ap-invalid');
      bad = true;
    }
    if (!selectedType) {
      typeField?.classList.add('ap-invalid');
      bad = true;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail)) {
      emailField?.classList.add('ap-invalid');
      bad = true;
    }
    if (bad) return;
    setStatus('', '');
    showPane(step2);
  });

  // ── Submit (Finish or Skip) ────────────────────────────────────
  const finishBtn = q<HTMLButtonElement>('#ap-finish');
  const skipBtn = q<HTMLButtonElement>('#ap-skip');

  const orNull = (sel: string): string | null => {
    const v = q<HTMLInputElement | HTMLTextAreaElement>(sel)?.value.trim() ?? '';
    return v === '' ? null : v;
  };

  const submit = async (includeDetails: boolean): Promise<void> => {
    const businessName = nameInput?.value.trim() ?? '';
    const contactEmail = (emailInput?.value.trim() ?? '').toLowerCase();

    const payload: ApplicationPayload = {
      business_name: businessName,
      business_type: selectedType,
      description: includeDetails ? orNull('#description') : null,
      contact_email: contactEmail,
      contact_phone: null,
      website: includeDetails ? orNull('#website') : null,
      instagram_handle: includeDetails
        ? (orNull('#instagram_handle')?.replace(/^@/, '') ?? null)
        : null,
      instagram_follower_count: null,
      facebook_handle: null,
      tiktok_handle: null,
      address: null,
      city: includeDetails ? orNull('#city') : null,
      country: null,
      experience: null,
      why_join: null,
    };

    if (finishBtn) finishBtn.disabled = true;
    if (skipBtn) skipBtn.disabled = true;
    setStatus('Submitting…', '');

    try {
      await submitApplication(payload, accessToken);
      setStatus('', '');
      const title = q<HTMLElement>('#ap-success-title');
      const emailOut = q<HTMLElement>('#ap-success-email');
      if (title) title.textContent = `You're in, ${businessName}!`;
      if (emailOut) emailOut.textContent = contactEmail;
      showPane(step3);
    } catch (err) {
      setStatus(
        err instanceof Error ? err.message : 'Something went wrong — please try again.',
        'error',
      );
      if (finishBtn) finishBtn.disabled = false;
      if (skipBtn) skipBtn.disabled = false;
    }
  };

  if (emailInput && sessionEmail && emailInput.value.trim() === '') {
    emailInput.value = sessionEmail;
  }

  finishBtn?.addEventListener('click', () => void submit(true));
  skipBtn?.addEventListener('click', () => void submit(false));
}
